import React, { useCallback, useEffect, useMemo } from 'react';
import { usePlacesWidget } from 'react-google-autocomplete';
import { getLang } from 'i18n/locale';
import { useOTSTranslation } from 'i18n/useOTSTranslation';

import styles from './input.module.scss';

export type IProps = {
  isCustom: boolean,
  positionError: boolean,
  initialLocationTitle?: string,
  setLocationCoords: (location?: { latitude: number, longitude: number, title: string }) => void,
};

export const NearbyLocationInput = ({
  positionError, isCustom, setLocationCoords, initialLocationTitle,
}: IProps) => {
  const { t } = useOTSTranslation('common');

  const { ref } = usePlacesWidget<HTMLInputElement>({
    apiKey: process.env.NEXT_PUBLIC_GOOGLE_API_5_KEY,
    onPlaceSelected: (place) => {
      const latitude = place?.geometry?.location?.lat();
      const longitude = place?.geometry?.location?.lng();
      const address = place?.formatted_address;
      if (typeof latitude !== 'undefined' && typeof longitude !== 'undefined' && typeof address !== 'undefined') {
        setLocationCoords({ latitude, longitude, title: address });
      }
    },
    language: getLang(),
  });

  const placeholder = useMemo(() => {
    if (isCustom) {
      return t('nearbyResorts.searchPlace');
    }
    if (positionError) {
      return t('nearbyResorts.searchPlace');
    }
    return t('nearbyResorts.currentLocation');
  }, [positionError, isCustom]);

  useEffect(() => {
    ref.current!.value = initialLocationTitle || '';
  }, [initialLocationTitle]);

  const resetSearch = useCallback(() => {
    setLocationCoords(undefined);
  }, []);

  const checkValue = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target?.value) {
      resetSearch();
    }
  }, []);

  return (
    <div>
      <div>
        <input ref={ref} className={styles.textInput} placeholder={placeholder} onChange={checkValue} />
        {initialLocationTitle && !positionError && (
          <div
            tabIndex={-1}
            role="button"
            onClick={resetSearch}
            className={styles.reset}
          >
            {t('nearbyResorts.useCurrentLocation')}
          </div>
        )}
      </div>
      {positionError && <div className={styles.warning}>{t('nearbyResorts.noLocation')}</div>}
    </div>
  );
};
